import {AdminHealthLink} from "../GenerationHealth/GenerationHealth";
import Navbar from "../Navbar/navbar";
import React, { useEffect, useState } from "react";
import "./Account.css";

import account from '../../assets/hamburgermenu_icons/account.svg'
import { TextField, Button, MenuItem } from "@mui/material";
import supabase from "../../supabaseClient/supabaseClient";
import { roles, countries } from "./dropdowns";
import { useAuth } from "../Login/AuthContext";
import { countWorksheets } from "../../data/worksheetsApi";

const formatDate = (isoString) => {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Account = () => {
  // Despite the name, AuthContext's worksheetsCount is the user's credits
  // balance (it reads profiles.credits) — aliased here to avoid confusion
  // with the actual saved-worksheets count fetched below.
  const { user, worksheetsCount: creditsBalance } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [worksheetsMade, setWorksheetsMade] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "",
    school: "",
    country: "",
  });
  const [initialData, setInitialData] = useState({});
  const [isFormModified, setIsFormModified] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    checkFormModified({ ...formData, [name]: value });
  };

  const checkFormModified = (currentData) => {
    setIsFormModified(
      JSON.stringify(currentData) !== JSON.stringify(initialData)
    );
  };

  const fetchProfileData = async () => {
    const {
      data: { session },
      error: sessionError,
    } = await supabase.auth.getSession();

    if (sessionError) {
      console.error("Error fetching session:", sessionError.message);
      return;
    }

    if (session) {
      const { email } = session.user;

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("email", email)
          .single();

        if (error && error.code !== "PGRST116") {
          // Handle unexpected errors except "no row found"
          console.error("Error fetching profile:", error.message);
        } else if (data) {
          // Handle cases where data exists but may contain null fields
          const sanitizedData = {
            email: data.email || email,
            name: data.name || "",
            role: data.role || "",
            school: data.school || "",
            country: data.country || "",
          };

          setFormData(sanitizedData);
          setInitialData(sanitizedData);
        } else {
          // No profile data found, set defaults
          const defaultData = {
            email,
            name: "",
            role: "",
            school: "",
            country: "",
          };

          setFormData(defaultData);
          setInitialData(defaultData);
        }
      } catch (error) {
        console.error("Unexpected error:", error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { name, role, school, country } = formData;
      const { error } = await supabase
        .from("profiles")
        .update({ name, role, school, country })
        .eq("id", user.id);
      if (error) {
        console.error("Error updating profile:", error.message);
      } else {
        console.log("Profile updated successfully");
        setInitialData(formData); // Sync initialData with submitted form
        setIsFormModified(false);
        setIsEditing(false);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    if (!user) return;
    countWorksheets(user.id).then(({ count }) => setWorksheetsMade(count));
  }, [user]);

  return (
    <div className="account-page-body">
      <Navbar />

      <AdminHealthLink/>
      <div className="account-content">
        <div className="account-header">
          <img src={account} alt="Profile" className="account-icon" />
          <h1 className="account-page-title">Profile</h1>
        </div>
        <div className="account-stats">
          <div className="account-stat">
            <p className="account-stat-value">{formatDate(user?.created_at)}</p>
            <p className="account-stat-label">Member since</p>
          </div>
          <div className="account-stat">
            <p className="account-stat-value">{worksheetsMade ?? "—"}</p>
            <p className="account-stat-label">Worksheets created</p>
          </div>
          <div className="account-stat">
            <p className="account-stat-value">{creditsBalance ?? "—"}</p>
            <p className="account-stat-label">Credits remaining</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="account-card">
          <TextField
            className="email-input"
            label="Email"
            name="email"
            value={formData.email}
            margin="normal"
            disabled
          />
          <div className="profile-details">
            <TextField
              className="name-input"
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
              disabled={!isEditing}
            />
            <TextField
              className="role-select"
              select
              label="Academic Role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              disabled={!isEditing}
            >
              {roles.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              className="school-input"
              label="School"
              name="school"
              value={formData.school}
              onChange={handleChange}
              margin="normal"
              required
              disabled={!isEditing}
            />
            <TextField
              className="country-select"
              select
              label="Country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              margin="normal"
              required
              disabled={!isEditing}
            >
              {countries.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
          {!isEditing ? (
            <Button
              type="button"
              className="edit-button"
              variant="contained"
              color="primary"
              onClick={() => setIsEditing(true)}
              sx={{ backgroundColor: '#3f4041', '&:hover': { backgroundColor: '#5a5b5d' } }}
            >
              Edit
            </Button>
          ) : (
            <Button
              className="submit-button"
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormModified}
              sx={{ backgroundColor: '#22c55e', '&:hover': { backgroundColor: '#16a34a' } }}
            >
              Submit
            </Button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Account;
